<template>
	<o-card
		v-tooltip="picto.tooltip ? picto.tooltip : !label ? picto.label : undefined"
		class="picto-card"
		:selected="selected"
		:class="[
			{
				'picto-card--error': error,
				'picto-card--selected': selected,
				'picto-card--selectable': selectable,
				'picto-card--callback': picto.callback,
				'picto-card--marker': marker,
			},
			`picto-card--${color}`,
		]"
		@click="setup.handleClick()">
		<div class="picto-card__content">
			<o-icon
				v-if="closable"
				ripple="danger"
				class="picto-card__close"
				font-icon="icon-interface-delete-1"
				@click="emits('delete', picto)"/>
			<o-icon
				class="picto-card__icon"
				:font-icon="`icon-${picto.icon}`"/>
			<small
				v-if="picto.label && label"
				class="picto-card__label">
				<slot>{{ picto.label }}</slot>
			</small>
		</div>
	</o-card>
</template>

<script setup lang="ts">
// #region Setup
import type { SharedPictoCardEmits, SharedPictoCardProps } from './SharedPictoCardSetup';
import SharedPictoCardSetup from './SharedPictoCardSetup';
const emits = defineEmits<SharedPictoCardEmits>() as SharedPictoCardEmits;
const props = withDefaults(defineProps<SharedPictoCardProps>(), SharedPictoCardSetup.defaultProps);
const setup = new SharedPictoCardSetup(props, emits);
// #endregion
</script>

<style scoped lang="less">
@import "@/styles/variables";
@import "@/styles/mixins.less";

.picto-card {
	position: relative;
	color: var(--grey);
	background: rgba(var(--rgb-grey-light), 0.4);
	box-shadow: none;

	:deep(.orion-card__body) {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0.25rem;
	}

	&__alert-icon {
		position: absolute;
		right: calc(50% - 0.6rem);
		bottom: -1.25rem;
		font-size: 1.25rem;
		color: var(--warning);
	}

	&__content {
		.flex-align(center, center);

		position: relative;
		flex: 1;
		flex-flow: column wrap;
		gap: 0.3rem;
		height: 100%;
		padding: 0.3rem 0;
		font-size: 1.75rem;
	}

	&__close {
		position: absolute;
		top: 0.1rem;
		right: 0;
		font-size: 0.45rem;
		color: var(--grey-darker);

		&:hover {
			color: var(--danger);
		}
	}

	&__icon {
		align-self: center;
	}

	&__label {
		flex: 1;
		max-width: 100%;
		font-size: 0.6rem;
		line-height: 1.1;
		color: var(--grey-dark);
		text-align: center;
	}

	img,
	svg {
		.square(1.5rem);

		display: block;
	}

	&--selected {
		> .orion-card__selected {
			transform: scale(0.7);

			&::before,
			&::after {
				display: none;
			}
		}
	}

	&--selectable,
	&--callback {
		cursor: pointer;
	}

	&--marker {
		&::after {
			.circle(0.5rem);

			content: "";
			position: absolute;
			top: -0.125rem;
			right: -0.125rem;
			display: block;
			background: var(--warning);
		}
	}

	&--error {
		outline: 0.06rem solid var(--danger);
	}

	each(@colorsAndGreys, {
	@color: ~"--@{value}";
	@colorDark: ~"--@{value}-dark";
	@colorRgba: ~"--rgb-@{value}";
	&--@{value} {
		background-color: rgba(var(@colorRgba), 0.25);
		color: var(@color);
		.picto-card__label {
			color: var(@colorDark);
		}
	}
})
	&--pending {
		color: var(--warning-dark);
		background: rgba(var(--rgb-warning), 0.15);
	}

	&:not(.picto-card--selectable, .picto-card--callback):hover {
		transform: none !important;
		box-shadow: none !important;
	}
}
</style>
